import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getProjects, Project } from '../services/projects';

function Projects() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await getProjects({
          page: 1,
          page_size: 100,
          status_filter: statusFilter !== 'all' ? statusFilter : undefined,
        });
        setProjects(response.projects);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching projects:', err);
        setError(err.response?.data?.detail || 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [statusFilter]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Status filter already applied in API call
    return matchesSearch;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'ready':
        return 'badge-success';
      case 'provisioning':
        return 'badge-warning';
      case 'failed':
        return 'badge badge-danger';
      default:
        return 'badge';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]">
      <Sidebar />

      <div className="ml-64 p-8">
        {/* Header */}
        <header className="mb-8 fade-in-up">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-display mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Projects
              </h1>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Manage and monitor all your infrastructure projects
              </p>
            </div>
            <Link to="/projects/new" className="btn-primary">
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Project
              </span>
            </Link>
          </div>
        </header>

        {/* Filters */}
        <div className="card mb-6 fade-in-up delay-100">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                Search projects
              </label>
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name or owner..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-bg-light)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                Filter by status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                style={{
                  backgroundColor: 'var(--color-bg-light)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
              >
                <option value="all">All statuses</option>
                <option value="ready">Ready</option>
                <option value="provisioning">Provisioning</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="card mb-6 bg-red-50 border border-red-200 fade-in-up delay-200">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="card fade-in-up delay-200">
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">Loading projects...</p>
            </div>
          </div>
        )}

        {/* Projects Table */}
        {!loading && (
          <div className="card fade-in-up delay-200">
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Project Name</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Status</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Type</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Owner</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Created</th>
                  <th className="text-right py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => (
                  <tr key={project.id} className="border-b transition-colors hover:bg-[var(--color-bg-elevated)]" style={{ borderColor: 'var(--color-border)' }}>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 113, 170, 0.1)' }}>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: 'var(--color-primary)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                        </div>
                        <span className="font-mono text-sm" style={{ color: 'var(--color-text-primary)' }}>{project.name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`badge ${getStatusBadgeClass(project.status)} text-xs`}>
                        {project.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="badge badge-primary text-xs">{project.type}</span>
                    </td>
                    <td className="py-4 px-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {project.assigned_users && project.assigned_users.length > 0
                        ? project.assigned_users[0].email
                        : project.owner_auth0_sub.substring(0, 20)}
                    </td>
                    <td className="py-4 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      {new Date(project.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="p-2 rounded hover:bg-[var(--color-bg-elevated)] transition-colors" title="View details">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </button>
                        <button className="p-2 rounded hover:bg-[var(--color-bg-elevated)] transition-colors" title="Edit project">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button className="p-2 rounded hover:bg-red-900/20 transition-colors" title="Delete project">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: '#DC2626' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredProjects.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <p className="text-lg font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>No projects found</p>
              <p style={{ color: 'var(--color-text-secondary)' }}>Try adjusting your search or filters</p>
            </div>
          )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Projects;
